import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Users, Mail, Phone, Tag, Loader2, Inbox } from "lucide-react";
import { AntennaLogo } from "@/components/site/AntennaLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("ns_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    axios
      .get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setLeads(data))
      .catch((e) => {
        if (e.response?.status === 401) {
          localStorage.removeItem("ns_token");
          navigate("/admin/login");
        } else {
          setLeads([]);
        }
      });
  }, [token, navigate]);

  const logout = () => {
    localStorage.removeItem("ns_token");
    navigate("/admin/login");
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="grain min-h-screen bg-[#05050A] font-body text-white">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#05050A]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 md:px-8">
          <div className="flex items-center gap-2">
            <AntennaLogo className="h-5 w-5" />
            <span className="font-display font-black tracking-tighter">
              NOVA<span className="text-[#8B5CF6]">STREAM</span>
            </span>
            <span className="ml-2 font-mono-accent text-xs uppercase tracking-widest text-slate-500">/ Painel</span>
          </div>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-display text-sm font-bold transition-colors hover:border-[#8B5CF6]"
          >
            <LogOut className="h-4 w-4" /> Sair
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 md:px-8">
        <div className="mb-8 flex items-center gap-3">
          <div className="rounded-2xl bg-[#8B5CF6]/15 p-3">
            <Users className="h-6 w-6 text-[#a78bfa]" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-black uppercase tracking-tighter md:text-3xl">Leads recebidos</h1>
            <p className="font-mono-accent text-xs uppercase tracking-widest text-slate-500" data-testid="leads-count">
              {leads === null ? "Carregando..." : `${leads.length} contato(s)`}
            </p>
          </div>
        </div>

        {leads === null ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-white/8 bg-[#0F0F1A] py-20 text-center">
            <Inbox className="h-10 w-10 text-slate-600" />
            <p className="mt-4 font-body text-slate-400">Nenhum lead ainda. Os contatos do formulário aparecerão aqui.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {leads.map((l) => (
              <div key={l.id} data-testid={`lead-card-${l.id}`} className="rounded-2xl border border-white/8 bg-[#0F0F1A] p-6">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-display text-lg font-bold">{l.name}</h3>
                  {l.plan && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#8B5CF6]/15 px-3 py-1 font-mono-accent text-[10px] uppercase tracking-widest text-[#a78bfa]">
                      <Tag className="h-3 w-3" /> {l.plan}
                    </span>
                  )}
                </div>
                <div className="mt-3 space-y-2 font-body text-sm text-slate-300">
                  <a href={`mailto:${l.email}`} className="flex items-center gap-2 hover:text-[#06B6D4]">
                    <Mail className="h-4 w-4 text-slate-500" /> {l.email}
                  </a>
                  {l.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-slate-500" /> {l.phone}
                    </div>
                  )}
                  {l.message && <p className="mt-2 rounded-lg bg-white/5 p-3 text-slate-300">{l.message}</p>}
                </div>
                <p className="mt-4 font-mono-accent text-[10px] uppercase tracking-widest text-slate-600">{fmt(l.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
